/**
 * Transform form data with school subjects into pedagogical objectives format
 * 
 * @param {Object} formData - Form data containing support type and subjects
 * @param {string} formData.desiredSupport|formData.support_type - Support type
 * @param {Array} formData.prioritizedSchoolSubjects|formData.priority_subjects|formData.selected_subjects - Array of school subject IDs
 * @param {Object} formData.subjectHoursAllocation|formData.subject_hours_allocation - Allocation of hours per subject (optional)
 * @param {number} hoursPerWeek - Total hours per week
 * @param {Array} schoolSubjects - Full list of school subjects with details
 * @returns {Array} objectives - Array of pedagogical objectives
 */
export const transformFormDataToObjectives = (formData, hoursPerWeek, schoolSubjects = []) => {
    const objectives = []
    
    // Map desired support to support type (handle both formats)
    const supportTypeMap = {
        "Aide aux devoirs": "homework_help",
        "Soutien sur une ou plusieurs matières": "subject_support"
    }
    
    const rawSupportType = formData.support_type || formData.desiredSupport
    const supportType = supportTypeMap[rawSupportType] || rawSupportType || "subject_support"
    
    // Handle different field names for subject lists
    const subjectIds = formData.prioritizedSchoolSubjects || 
                      formData.priority_subjects || 
                      formData.selected_subjects || []

    if (supportType === "homework_help") {
        // Create a single homework objective with all selected subjects
        const subjects = schoolSubjects.filter((s) => subjectIds.includes(s.id))

        if (subjectIds.length > 0) {
            objectives.push({
                type: "homework",
                subject_ids: subjectIds,
                allocated_hours: hoursPerWeek,
                needs: [], // homework help doesn't need specific needs
                formulated_goal:
                    "Faire ses devoirs en " +
                    subjects.map((s) => s.name).join(", "),
            })
        }
    } else if (supportType === "subject_support") {
        // When more subjects than hours: single homework objective
        // When subjects <= hours: one level_up objective per subject
        if (subjectIds.length > hoursPerWeek) {
            const subjects = schoolSubjects.filter((s) => subjectIds.includes(s.id))
            objectives.push({
                type: "homework",
                subject_ids: subjectIds,
                allocated_hours: hoursPerWeek,
                needs: [],
                formulated_goal:
                    "Aide aux devoirs en " +
                    subjects.map((s) => s.name).join(", "),
            })
        } else {
            // Create one objective per selected subject with allocated hours
            let hoursAllocation = formData.subjectHoursAllocation ||
                                formData.subject_hours_allocation || {}

            // If no allocation data (step was skipped), generate it automatically
            if (
                Object.keys(hoursAllocation).length === 0 &&
                subjectIds.length > 0
            ) {
                const baseHours = Math.floor(hoursPerWeek / subjectIds.length)
                const extraHours = hoursPerWeek % subjectIds.length

                hoursAllocation = {}
                subjectIds.forEach((subjectId, index) => {
                    hoursAllocation[subjectId] =
                        baseHours + (index < extraHours ? 1 : 0)
                })
            }

            subjectIds.forEach((subjectId) => {
                const allocatedHours = hoursAllocation[subjectId] || 1
                const subjectNeeds = formData[`needs_subject_${subjectId}`] || []
                const subject = schoolSubjects.find((s) => s.id === subjectId)

                if (subject) {
                    objectives.push({
                        type: "level_up",
                        subject_ids: [subjectId],
                        allocated_hours: allocatedHours,
                        needs: subjectNeeds,
                        formulated_goal: "Améliorer son niveau en " + subject.name,
                    })
                }
            })
        }
    }

    return objectives
}

